import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import { trackClick } from '../utils/analytics';
import { sendContactMessage, type ContactMessage } from '../utils/contactUs';

const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';
const BLUE_ACCENT = '#3B7BF4';

const ContactUsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.startsWith('he') ?? false;

  const [form, setForm] = useState<ContactMessage>({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    trackClick('Contact Us Submit', 'ContactUs');
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      await sendContactMessage(form);
      setSubmitStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('sendContactMessage error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="flex flex-col flex-grow"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <header
        className="px-2 relative"
        style={{ background: '#FFFFFF', borderBottom: `1px solid ${BORDER_LIGHT}` }}
      >
        <h1 className="text-lg font-semibold text-center" style={{ color: TEXT_DARK }}>
          {t('contact_form_title')}
        </h1>
      </header>

      <main className="flex-1 px-4 pt-5 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+90px)]">
        {submitStatus === 'success' ? (
          <div className="rounded-2xl p-6 text-center" style={{ background: '#f0fdf4' }}>
            <span className="block mb-3 text-4xl">✓</span>
            <p className="font-semibold" style={{ color: '#166534' }}>
              {t('contact_form_success')}
            </p>
          </div>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div className="mb-4">
              <label
                className="mb-1.5 block text-[13px] font-semibold"
                style={{ color: TEXT_SECONDARY }}
              >
                {t('contact_form_name')}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="box-border w-full rounded-[10px] px-3.5 py-3 text-[15px] outline-none"
                style={{ border: `1px solid ${BORDER_LIGHT}` }}
                placeholder={t('contact_form_name_placeholder') as string}
              />
            </div>

            <div className="mb-4">
              <label
                className="mb-1.5 block text-[13px] font-semibold"
                style={{ color: TEXT_SECONDARY }}
              >
                {t('contact_form_email')}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="box-border w-full rounded-[10px] px-3.5 py-3 text-[15px] outline-none"
                style={{ border: `1px solid ${BORDER_LIGHT}` }}
                placeholder={t('contact_form_email_placeholder') as string}
              />
            </div>

            <div className="mb-5">
              <label
                className="mb-1.5 block text-[13px] font-semibold"
                style={{ color: TEXT_SECONDARY }}
              >
                {t('contact_form_message')}
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                className="box-border w-full resize-y rounded-[10px] px-3.5 py-3 text-[15px] outline-none"
                style={{ border: `1px solid ${BORDER_LIGHT}`, fontFamily: 'inherit' }}
                placeholder={t('contact_form_message_placeholder') as string}
              />
            </div>

            {submitStatus === 'error' && (
              <p className="mb-3 text-[13px]" style={{ color: '#ef4444' }}>
                {t('contact_form_error')}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-xl px-5 py-3.5 text-base font-semibold text-white"
              style={{
                background: BLUE_ACCENT,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? t('contact_form_sending') : t('contact_form_submit')}
            </button>
          </form>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default ContactUsPage;
